import React, {useState, useEffect} from 'react';
import MainLayout from '../../../../mainLayout';
import styled, {css, useTheme} from 'styled-components/native';
import {ScreenProps} from '../../../../../interfaces/screens/mainScreen';
import {tr} from '../../../../../translations';
import useResponsiveDimensions from '../../../../../utils/hooks/useResponsiveDimensions';
import TextGeneric from '../../../../../components/textGeneric';
import GenericButton from '../../../../../components/button';
import {isWeb} from '../../../../../utils/responsive';
import {Theme} from '../../../../../theme/lightTheme';
import {getFontFamily} from '../../../../../utils/helpers';
import Icon from '../../../../../components/icon';
import {
  ActivityIndicator,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from 'react-native';
import screens from '../../../../../config/screens';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import {ViewPatientProps} from '../../../../../interfaces/screens/viewPatient';
import {
  PatientReportType,
  PatientType,
} from '../../../../../interfaces/api/content/ehr';
import {
  deletePatient,
  deletePatientReport,
  getPatient,
  getPatientReports,
  getPatients,
} from '../actions';
import TableRowSkeleton from '../../../../../components/tableRowSkeleton';
import EmptyList from '../../../../../components/emptyList';
import {EmptyContainer} from '../../../home';
import Loader from '../../../../../components/loader';
import SearchListSkeleton from '../../../../../components/searchListSkeleton';
import {openQuestionModal} from '../../../../../connected-components/questionModal/actions';
import dayjs from 'dayjs';

const Container = styled.View`
  width: 100%;
  flex: 1;
  position: relative;
  padding-top: ${({theme}) => theme.text.s55 * 2}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-horizontal: ${({theme}) => theme.text.s55}px;
`;

const PatientCardContainer = styled.View`
  width: 100%;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 10px;
  margin-bottom: 24px;
  shadow-opacity: 0.1;
  shadow-radius: 10px;
  padding: ${({theme}) => theme.text.s55 * 1.5}px;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 2px;
  elevation: 2;
`;

const HeaderContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
  padding-bottom: 12px;
`;

const SectionTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s5}px;
  color: ${({theme}) => theme.text.title};
  margin-bottom: 8px;
  ${isWeb
    ? css`
        font-weight: bold;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

const PatientName = styled(TextGeneric)`
  font-size: 24px;
  color: ${({theme}) => theme.text.title};
`;

const AddButton = styled(GenericButton)`
  max-width: 170px;
  align-self: flex-end;
  margin-bottom: 16px;
`;

const InfoGrid = styled.View`
  flex-direction: ${isWeb ? 'row' : 'column'};
  flex-wrap: wrap;
  margin-top: 8px;
`;

const InfoColumn = styled.View`
  flex: 1;
  min-width: ${isWeb ? '200px' : 'auto'};
  margin-bottom: 16px;
  margin-right: ${isWeb ? '16px' : '0px'};
`;

const InfoSection = styled.View`
  margin-bottom: 16px;
`;

const InfoLabel = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  margin-bottom: 4px;
  ${isWeb
    ? css`
        font-weight: bold;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

const InfoValue = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
`;

const ReportsContainer = styled.View`
  width: 100%;
`;

const TableHeader = styled.View`
  flex-direction: row;
  padding-vertical: ${({theme}) => theme.text.s55}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
  background-color: ${({theme}) => theme.patients.headerBackground};
`;

const TableCell = styled.View<{flex: number}>`
  flex: ${({flex}) => flex};
  padding-horizontal: ${({theme}) => theme.text.s55}px;
`;

const TableHeaderText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  text-transform: uppercase;
`;

const TableRow = styled.View`
  flex-direction: row;
  padding-vertical: 12px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
`;

const TableBodyText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
`;

const ActionsContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const IconContainer = styled.TouchableOpacity`
  padding: ${({theme}) => theme.text.s55 / 2}px;
  background-color: ${({theme}) => theme.settings.icon + '11'};
  border-radius: 4px;
  margin-horizontal: ${({theme}) => theme.text.s55 / 4}px;
`;

const EmptyListContainer = styled.View`
  padding: 40px 20px;
  align-items: center;
  justify-content: center;
`;

const EmptyListText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.grey};
  text-align: center;
  margin-top: 16px;
`;

const LoaderContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

// Card view styled components for mobile
const ReportCard = styled.View`
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 8px;
  margin-bottom: 12px;
  padding: ${({theme}) => theme.text.s55}px;
  border-width: 1px;
  border-color: ${({theme}) => theme.border};
`;

const CardRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding-vertical: 4px;
`;

const CardLabel = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  ${isWeb
    ? css`
        font-weight: bold;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

const CardValue = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  flex: 1;
  text-align: right;
`;

const CardActionsContainer = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 8px;
  padding-top: 8px;
  border-top-width: 1px;
  border-top-color: ${({theme}) => theme.border};
`;

const loadMoreThreshold = 0.75; // Trigger when 75% scrolled

/**
 * ViewPatient screen component.
 *
 * @param {any} props - The screen component props.
 */
const ViewPatient: React.FC<ViewPatientProps> = ({route}) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {patient} = route?.params || {};
  const {isMobile} = useResponsiveDimensions();
  const [data, setData] = useState<PatientReportType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const loadMore = async (refresh = false) => {
    if (!patient?.id) {
      setIsError(true);
      return;
    }
    if (isLoading && !refresh) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const result = await getPatientReports(patient?.id, refresh ? 1 : page);
      if (!result) {
        setIsError(true);
        setIsLastPage(true);
        setIsLoading(false);
        return;
      }
      setData(prev => {
        if (refresh) {
          return [...result?.data!];
        } else {
          return [...prev, ...result?.data!].filter((value, index, self) => {
            return (
              self.findIndex(obj =>
                //@ts-ignore
                ['id'].every(key => obj[key] === value[key]),
              ) === index
            );
          });
        }
      });
      setPage(prev => (refresh ? 2 : prev + 1));
      const pageSize = 20;
      const totalPageCount = Math.ceil(
        parseFloat(result?.total ? result?.total + '' : '2') / pageSize,
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMore(true);
  }, []);

  // Web scroll handler that manages both animation and pagination
  const webScrollHandler = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // Calculate if we need to load more content
    const {contentOffset, contentSize, layoutMeasurement} = event.nativeEvent;
    // Calculate scroll percentage - how far down the user has scrolled
    const scrollPercentage =
      (contentOffset.y + layoutMeasurement.height) / contentSize.height;

    // If we've scrolled past the threshold, load more content
    if (scrollPercentage >= loadMoreThreshold && !isLoading && !isLastPage) {
      loadMore();
    }
  };

  // Function to handle viewing a report
  const handleViewReport = (report: PatientReportType) => {
    navigation.navigate(screens.content.viewPatientReport, {
      report,
      callback: () => loadMore(true),
    });
  };

  // Function to handle adding a new report
  const handleAddReport = () => {
    navigation.navigate(screens.content.addEditPatientReport, {
      patientId: patient?.id,
      callback: () => loadMore(true),
    });
  };

  // Function to handle deleting a report
  const handleDeleteReport = (report: PatientReportType) => {
    openQuestionModal({
      title: 'Delete Patient Report',
      message:
        "Are you sure you want to delete this patient's report? This action cannot be undone.",
      type: 'question',
      onConfirm: async () => {
        const res = await deletePatientReport(report?.id);
        if (res) {
          const tempData = data.filter(i => i.id !== report.id);
          setData(tempData);
        }
      },
    });
  };

  // Function to handle editing a report
  const handleEditReport = (report: PatientReportType) => {
    navigation.navigate(screens.content.addEditPatientReport, {
      report,
      callback: () => loadMore(true),
    });
  };

  // Render patient info
  const renderPatientInfo = () => {
    if (!patient) return null;

    return (
      <PatientCardContainer>
        <HeaderContainer>
          <PatientName bold>{patient.name}</PatientName>
        </HeaderContainer>

        <SectionTitle>{tr('patients.patientInfo')}</SectionTitle>

        <InfoGrid>
          <InfoColumn>
            <InfoSection>
              <InfoLabel>{tr('patients.patientEmail')}</InfoLabel>
              <InfoValue>{patient.email}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>{tr('patients.patientDob')}</InfoLabel>
              <InfoValue>{dayjs(patient.dob).format('YYYY-MM-DD')}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>{tr('patients.patientGender')}</InfoLabel>
              <InfoValue>{patient.gender}</InfoValue>
            </InfoSection>
          </InfoColumn>

          <InfoColumn>
            <InfoSection>
              <InfoLabel>{tr('patients.patientPhone')}</InfoLabel>
              <InfoValue>{patient.phone}</InfoValue>
            </InfoSection>

            <InfoSection>
              <InfoLabel>{tr('patients.patientAddress')}</InfoLabel>
              <InfoValue>{patient.address}</InfoValue>
            </InfoSection>
          </InfoColumn>
        </InfoGrid>
      </PatientCardContainer>
    );
  };

  // Render reports table
  const renderReportsTable = () => (
    <PatientCardContainer style={{padding: 0, overflow: 'hidden'}}>
      <View
        style={{
          paddingHorizontal: theme.text.s55 * 1.5,
          paddingTop: theme.text.s55 * 1.5,
        }}>
        <HeaderContainer style={{marginBottom: 1}}>
          <SectionTitle>{tr('patients.reports')}</SectionTitle>
          <AddButton
            outline
            label={tr('patients.addReport')}
            labelStyle={{
              color: theme.text.dark,
            }}
            onPress={handleAddReport}
          />
        </HeaderContainer>
      </View>

      {(isError || data.length === 0) && !isLoading ? (
        <EmptyContainer>
          <EmptyList
            iconName={'empty'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            title={isError ? 'app.error' : 'app.empty'}
            onRefreshPress={() => loadMore(true)}
            showRefresh={isError}
          />
        </EmptyContainer>
      ) : (
        <ReportsContainer>
          {isMobile ? (
            // Mobile Card View
            <View style={{padding: theme.text.s55 * 1.5}}>
              {isLoading && data.length === 0 ? (
                <SearchListSkeleton
                  numberOfItems={10}
                  height={theme.text.s1 * 5}
                />
              ) : (
                data.map(report => (
                  <ReportCard key={report.id}>
                    <CardRow>
                      <CardLabel>{tr('patients.reportDate')}:</CardLabel>
                      <CardValue>
                        {dayjs(report.date).format('YYYY-MM-DD')}
                      </CardValue>
                    </CardRow>
                    <CardRow>
                      <CardLabel>{tr('patients.reportType')}:</CardLabel>
                      <CardValue>{report.form_label}</CardValue>
                    </CardRow>
                    <CardActionsContainer>
                      <IconContainer
                        accessibilityLabel={tr('patients.view')}
                        onPress={() => handleViewReport(report)}>
                        <Icon
                          name={'eyeOutline'}
                          size={theme.text.s55}
                          color={theme.settings.icon}
                        />
                      </IconContainer>
                      <IconContainer
                        accessibilityLabel={tr('patients.edit')}
                        onPress={() => handleEditReport(report)}>
                        <Icon
                          name={'edit'}
                          size={theme.text.s55}
                          color={theme.settings.icon}
                        />
                      </IconContainer>
                      <IconContainer
                        accessibilityLabel={tr('patients.delete')}
                        style={{backgroundColor: theme.text.error + '11'}}
                        onPress={() => handleDeleteReport(report)}>
                        <Icon
                          name={'delete'}
                          size={theme.text.s55}
                          color={theme.text.error}
                        />
                      </IconContainer>
                    </CardActionsContainer>
                  </ReportCard>
                ))
              )}
            </View>
          ) : (
            // Desktop Table View
            <>
              <TableHeader>
                <TableCell flex={1}>
                  <TableHeaderText bold>
                    {tr('patients.reportDate')}
                  </TableHeaderText>
                </TableCell>
                <TableCell flex={2}>
                  <TableHeaderText bold>
                    {tr('patients.reportType')}
                  </TableHeaderText>
                </TableCell>
                <TableCell flex={1}>
                  <TableHeaderText bold>
                    {tr('patients.actions')}
                  </TableHeaderText>
                </TableCell>
              </TableHeader>

              {isLoading && data.length === 0 ? (
                <TableRowSkeleton />
              ) : (
                data.map(report => (
                  <TableRow key={report.id}>
                    <TableCell flex={1}>
                      <TableBodyText>
                        {dayjs(report.date).format('YYYY-MM-DD')}
                      </TableBodyText>
                    </TableCell>
                    <TableCell flex={2}>
                      <TableBodyText>{report.form_label}</TableBodyText>
                    </TableCell>
                    <TableCell flex={1}>
                      <ActionsContainer>
                        <IconContainer
                          accessibilityLabel={tr('patients.view')}
                          onPress={() => handleViewReport(report)}>
                          <Icon
                            name={'eyeOutline'}
                            size={theme.text.s55}
                            color={theme.settings.icon}
                          />
                        </IconContainer>
                        <IconContainer
                          accessibilityLabel={tr('patients.edit')}
                          onPress={() => handleEditReport(report)}>
                          <Icon
                            name={'edit'}
                            size={theme.text.s55}
                            color={theme.settings.icon}
                          />
                        </IconContainer>
                        <IconContainer
                          accessibilityLabel={tr('patients.delete')}
                          style={{backgroundColor: theme.text.error + '11'}}
                          onPress={() => handleDeleteReport(report)}>
                          <Icon
                            name={'delete'}
                            size={theme.text.s55}
                            color={theme.text.error}
                          />
                        </IconContainer>
                      </ActionsContainer>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </>
          )}
          {isLoading && data.length !== 0 && (
            <View style={{padding: theme.text.s1}}>
              <Loader />
            </View>
          )}
        </ReportsContainer>
      )}
    </PatientCardContainer>
  );

  return (
    <MainLayout
      enableScroll={false}
      title={tr('patients.viewPatient')}
      backHeader
      menuHeader
      noPadding>
      <ScrollView onScroll={webScrollHandler}>
        <Container>
          <ContentContainer>
            {renderPatientInfo()}
            {renderReportsTable()}
          </ContentContainer>
        </Container>
      </ScrollView>
    </MainLayout>
  );
};

ViewPatient.displayName = 'ViewPatientScreen';
export default ViewPatient;
